import { EventEmitter, Injectable, inject } from '@angular/core';

import { UserLoginData } from '../interfaces/user-login-data.interface';
import { User } from '../classes/user';
import { LoginApiService } from './api';
import { AccessToken } from './api/general/access-token';
import { WindowToken } from '../providers/window.provider';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class LoginService {
  private readonly loginApi = inject(LoginApiService);
  private window = inject<Window>(WindowToken);

  public static readonly USER_STORAGE_KEY = 'user';
  public userChanged = new EventEmitter<User | undefined>();

  private _user: User = null;
  private _userLoginData: UserLoginData = {};

  constructor() {
    this.initUser();
  }

  /**
   * The currently logged in user. If no user is logged in, this object will be null.
   */
  public get user(): User {
    return this._user;
  }

  public get emailAddress(): string {
    return this._userLoginData.emailAddress;
  }

  public get password(): string {
    return this._userLoginData.password;
  }

  public enterEmailAddress(emailAddress: string): void {
    this._userLoginData.emailAddress = emailAddress;
  }

  public enterPassword(password: string): void {
    this._userLoginData.password = password;
  }

  public enterVerificationCode(verificationCode: string): void {
    this._userLoginData.verificationCode = verificationCode;
  }

  /**
   * Log the user in. If successful, it will emit a log event with the newly logged in user.
   * @returns An observable of the newly logged in user, or an error if they could not be logged in.
   */
  public async login(): Promise<User> {
    const accessToken = await firstValueFrom(this.loginApi.getAccessToken(this._userLoginData), { defaultValue: undefined });
    this._userLoginData = {};
    return this.initialiseRealms(accessToken);
  }

  public async loginWithCode(code: string): Promise<User> {
    const accessToken = await firstValueFrom(this.loginApi.getAccessTokenWithCode(code), { defaultValue: undefined });
    return this.initialiseRealms(accessToken);
  }

  /**
   * Log the user out and delete them from storage. Emits an empty log event.
   */
  public logout(): void {
    this._user = undefined;
    this.removeUserFromStorage();
    this.userChanged.emit();
  }

  private async initialiseRealms(accessToken: AccessToken): Promise<User> {
    const realms = await firstValueFrom(this.loginApi.getRealms(accessToken), { defaultValue: undefined });
    this._user = User.fromRealms(realms, accessToken);
    const defaultRealm = realms[0].id;
    await this.selectRealm(defaultRealm);

    const accountDetails = await firstValueFrom(this.loginApi.getSelf(this.user.realmAccessToken), { defaultValue: undefined });
    this.user.adminId = accountDetails.id;

    this.saveToStorage(this.user);
    this.userChanged.emit(this.user);
    return this.user;
  }

  public async selectRealm(realmId: string, redirectToAdmin = false): Promise<void> {
    const realmAccessToken = await firstValueFrom(this.loginApi.getRealmAccessToken(realmId, this.user.accessToken), { defaultValue: undefined });
    this.user.realmAccessToken = realmAccessToken;
    this.user.currentRealmId = realmId;
    if (this.user.enterpriseId) {
      this.user.enterprise = await firstValueFrom(this.loginApi.getEnterprise(this.user.enterpriseId, realmAccessToken), { defaultValue: undefined });
    }
    this.saveToStorage(this.user);

    if (redirectToAdmin) {
      this.window.location.href = '/admin';
    } else {
      this.userChanged.emit(this.user);
    }
  }

  /**
   * Initialise the user from storage.
   */
   private initUser() {
    this._user = this.loadUserFromStorage();
    if (this.user && !this.user.hasValidToken()) {
      this._user = null;
      this.removeUserFromStorage();
    }
    this.userChanged.emit(this._user);
  }
  /**
   * Load the user object from session storage.
   */
  private loadUserFromStorage(): User | null {
    const userJson = sessionStorage.getItem(LoginService.USER_STORAGE_KEY);
    if (userJson) {
      return User.deserialize(userJson);
    } else {
      return null;
    }
  }

  /**
   * Remove the record of the user from session storage.
   */
  private removeUserFromStorage(): void {
    sessionStorage.removeItem(LoginService.USER_STORAGE_KEY);
  }

  /**
   * Save the currently stored user object to session storage as the current user.
   */
  private saveToStorage(user: User) {
    sessionStorage.setItem(LoginService.USER_STORAGE_KEY, user.serialize());
  }

}
